import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import PropTypes from "prop-types";
import CsfMembrePageHeading
  from "../csf-membre-page-heading/csf-membre-page-heading";
import CsfMembreSideMenu from "../csf-membre-side-menu/csf-membre-side-menu";
import CsfMembreDossierUfcTable from "./CsfMembreDossierUfcTable";
import {COOKIE_LOGGED_IN_NAME, getCookie, setCookie} from "../utils/utils";
import {
  startLoadingSpinner,
  stopLoadingSpinner
} from "../../csf-ufc-pivotal/includes/utils";
import DrupalAccessTokenApi from "../../../js/csf/DrupalAccessTokenApi";
import CsfMembreDossierUfcDataLayer from "./CsfMembreDossierUfcDataLayer";

export function CsfMembreDossierUfc({ apiUrl, apiEnv, urlDossierUfc, urlNotifications }) {
  const [isError, setIsError] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [memberInfo, setMemberInfo] = useState({});
  const [ufcHistoryInfos, setUfcHistoryInfos] = useState([]);
  const [currentPeriodUfcList, setCurrentPeriodUfcList] = useState([]);
  const [previousPeriodUfcList, setPreviousPeriodUfcList] = useState([]);
  const [checkedPrintOption, setCheckedPrintOption] = useState('');
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [dataLayer, setDataLayer] = useState({});
  const langCode = (document?.documentElement?.lang === 'fr' ? 'fr-FR' : 'en-US');

  /*
  * Call this method when an api error occurs.
  * */
  function apiErrorCallback(errorMessage) {
    setIsError(true);
    stopLoadingSpinner();
  }

  useEffect(() => {
    startLoadingSpinner();
    fetchAccessToken();
  }, []);

  useEffect(() => {
    let cookie = getCookie(COOKIE_LOGGED_IN_NAME);
    if(cookie === "false" && showConsentModal){
      $('#membreConsentModal').modal('show');
      setCookie(COOKIE_LOGGED_IN_NAME, true, 1);
    }
  }, [showConsentModal]);

  function fetchAccessToken() {
    const drupalAccessTokenApi = new DrupalAccessTokenApi();
    drupalAccessTokenApi.fetchPostRequest()
      .then(response => {
        if (response?.api_access_token) {
          setAccessToken(response.api_access_token);
        }
        else {
          console.error('Error while fetching token');
        }
      })
      .catch(error => {
        console.error(error);
      });
  }

  useEffect(() => {
    if (accessToken) {
      const dataObj = new CsfMembreDossierUfcDataLayer(apiUrl, apiEnv, accessToken, apiErrorCallback);
      dataObj.fetchMemberInfo(setMemberInfo);
      setDataLayer(dataObj);
    }
  }, [accessToken]);

  useEffect(() => {
    if (accessToken && dataLayer?.isInit && memberInfo?.Contact_Id) {
      dataLayer.fetchContactInfo(memberInfo.Contact_Id, setUfcHistoryInfos);
      dataLayer.fetchCurrentPeriodUfcList(setCurrentPeriodUfcList);
      dataLayer.fetchPreviousPeriodUfcList(setPreviousPeriodUfcList);
    }
  }, [memberInfo, dataLayer]);

  function getCurrentPeriodHistoryInfo() {
    startLoadingSpinner();
    // La période courante et précédente sont affichées via le end point CSF_EM_UFC_History_Info.
    // Les périodes sont en ordre chronologique, le plus petit nombre est par conséquent la période précédente.
    if (ufcHistoryInfos.length > 1) {
      stopLoadingSpinner();
      return ufcHistoryInfos[1];
    }
    return null;
  }

  function getPreviousPeriodHistoryInfo() {
    startLoadingSpinner();
    // La période courante et précédente sont affichées via le end point CSF_EM_UFC_History_Info.
    // Les périodes sont en ordre chronologique, le plus petit nombre est par conséquent la période précédente.
    if (ufcHistoryInfos.length > 0) {
      stopLoadingSpinner();
      return ufcHistoryInfos[0];
    }
    return null;
  }

  function getPeriodSubtitle(period) {
    return ufcPeriodDateFormat(period?.Start_Date) + ' - ' + ufcPeriodDateFormat(period?.End_Date);
  }

  function getConformityFormatedDate(date) {
    return ufcPeriodDateFormat(date);
  }

  function ufcPeriodDateFormat(dateString) {
    // langCode: 'fr-FR' , 'en-US'

    if (dateString == null || dateString === '') {
      return '';
    }

    const date = new Date(dateString);

    const options = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    };

    return new Intl.DateTimeFormat(langCode, options).format(date);
  }

  return (
    <main className="membre home">

      <div className="content">
        {/* main section */}
        <section className="list primary primary-bg non-print-wrapper">

          {/* sub title */}

          <CsfMembrePageHeading
            title={window.Drupal.t('membre.dossierUFC.title')}
            memberName={memberInfo["Prénom"] + ' ' + memberInfo["Nom"]}
            certificatNumber={memberInfo["No_Dossier"]}
          />

          {isError && (
            <div className="alert alert-danger mb-4" role="alert"
              aria-live="polite">{window.Drupal.t('broker.manager.error.message')}</div>
          )}

          {getCurrentPeriodHistoryInfo() && (
            <div className="summary__period summary__period--current">
              <CsfMembreDossierUfcTable
                periodTitle={window.Drupal.t('membre.current-period.title')}
                periodSubTitle={getPeriodSubtitle(getCurrentPeriodHistoryInfo())}
                periodInOrder={getCurrentPeriodHistoryInfo()?.In_Order == "Oui" || getCurrentPeriodHistoryInfo()?.In_Order == "Yes"}
                ufcList={currentPeriodUfcList}
                collapse={false}
                setCheckedPrintOption={setCheckedPrintOption}
                printId="membre_print_summary_current"
                conformityEndDate={getConformityFormatedDate(getCurrentPeriodHistoryInfo()?.CoursObligatoire_EndDate || '')}
              />
            </div>
          )}

          {getPreviousPeriodHistoryInfo() && (
            <div className="summary__period summary__period--previous">
              <CsfMembreDossierUfcTable
                periodTitle={window.Drupal.t('membre.previous-period.title')}
                periodSubTitle={getPeriodSubtitle(getPreviousPeriodHistoryInfo())}
                periodInOrder={getPreviousPeriodHistoryInfo()?.In_Order == "Oui" || getPreviousPeriodHistoryInfo()?.In_Order == "Yes"}
                ufcList={previousPeriodUfcList}
                collapse={true}
                setCheckedPrintOption={setCheckedPrintOption}
                printId="membre_print_summary_previous"
                conformityEndDate={getConformityFormatedDate(getPreviousPeriodHistoryInfo()?.CoursObligatoire_EndDate || '')}
              />
            </div>
          )}
        </section>

        {/* aside section */}
        <CsfMembreSideMenu
          apiUrl={apiUrl}
          apiEnv={apiEnv}
          urlDossierUfc={urlDossierUfc}
          urlNotifications={urlNotifications}
          checkedPrintOption={checkedPrintOption}
          setCheckedPrintOption={setCheckedPrintOption}
          setShowConsentModal={setShowConsentModal}
        />

      </div>
    </main>
  );
}

window.addEventListener('DOMContentLoaded', () => {
  const domNode = document.getElementById('CsfMembreDossierUfc');

  if (domNode !== null) {
    const root = createRoot(domNode);

    const apiUrl = domNode.getAttribute("data-api-url");
    const apiEnv = domNode.getAttribute("data-api-env");
    const urlDossierUfc = domNode.getAttribute("data-urlSectionMembreDossierUfc");
    const urlNotifications = domNode.getAttribute("data-urlSectionMembreNotifications");

    root.render(<CsfMembreDossierUfc
      apiUrl={apiUrl}
      apiEnv={apiEnv}
      urlDossierUfc={urlDossierUfc}
      urlNotifications={urlNotifications}
    />);
  }
});

CsfMembreDossierUfc.propTypes = {
  apiUrl: PropTypes.string,
  apiEnv: PropTypes.string,
  urlDossierUfc: PropTypes.string,
  urlNotifications: PropTypes.string,
};
